// src/lib/purchaseCosts.ts
// Gemeinsame Kaufnebenkosten (GrESt, Notar, Grundbuch, Makler, Sonstiges) für alle Analyzer.
// Reine Funktionen, ohne React, damit Summen und Import-Fallbacks testbar sind.

/** Versionsfeld im Export. Ab Version 2 werden fehlende NK-Felder mit den aktuellen Defaults gefüllt. */
export const PURCHASE_COSTS_VERSION = 2;

export type PurchaseCostPcts = {
  grest: number;
  notar: number;
  grundbuch: number;
  makler: number;
  sonstiges: number;
};

/** Aktuelle Defaults (ab Version 2). Berlin als Default-Bundesland, passend zu den Beispieladressen. */
export const DEFAULT_BUNDESLAND = "Berlin";
// = GRESTE_SATZ["Berlin"] (Test prüft die Gleichheit)
export const NK_DEFAULT_PCTS: PurchaseCostPcts = {
  grest: 0.06,
  notar: 0.015,
  grundbuch: 0.005,
  makler: 0.0357,
  sonstiges: 0,
};

/**
 * Alte Defaults je Analyzer. Gelten nur beim Import von Dateien ohne Versionsfeld,
 * damit alte Analysen dieselben Summen zeigen wie früher.
 */
export const LEGACY_NK_PCTS = {
  etw: { grest: 0.065, notar: 0.015, grundbuch: 0.005, makler: 0.0357, sonstiges: 0.004 },
  efh: { grest: 0.065, notar: 0.015, grundbuch: 0.005, makler: 0.03, sonstiges: 0 },
  gewerbe: { grest: 0.065, notar: 0.015, grundbuch: 0.005, makler: 0.0357, sonstiges: 0 },
  mfh: { grest: 0.06, notar: 0.015, grundbuch: 0.005, makler: 0.0357, sonstiges: 0.004 },
  mixeduse: { grest: 0.065, notar: 0.01, grundbuch: 0.005, makler: 0, sonstiges: 0.005 },
} satisfies Record<string, PurchaseCostPcts>;

export type LegacyAnalyzer = keyof typeof LEGACY_NK_PCTS;

/** Summe der Prozentsätze in fester Reihenfolge (wie bisher in den Analyzern). */
export function pctSum(p: PurchaseCostPcts): number {
  return p.grest + p.notar + p.grundbuch + p.makler + p.sonstiges;
}

/**
 * Beträge je Position und Gesamtsumme. Ohne Rundung: die Analyzer runden wie bisher selbst.
 * `basis` ist die Bemessungsgrundlage des jeweiligen Analyzers (z. B. Kaufpreis oder Kaufpreisansicht).
 */
export function calcPurchaseCosts(basis: number, p: PurchaseCostPcts) {
  const sumPct = pctSum(p);
  return {
    grest: basis * p.grest,
    notar: basis * p.notar,
    grundbuch: basis * p.grundbuch,
    makler: basis * p.makler,
    sonstiges: basis * p.sonstiges,
    pctSum: sumPct,
    sumEur: basis * sumPct,
  };
}

/** Liest einen Prozentsatz aus einem Import-Objekt, sonst den Fallback. Ungültige Werte gelten als fehlend. */
function pick(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/**
 * Löst die NK-Werte beim Import auf. Fehlt ein Feld, gilt:
 *  - Datei mit Versionsfeld >= 2: aktuelle Defaults
 *  - Datei ohne Versionsfeld (alt): Legacy-Defaults des jeweiligen Analyzers
 * Bundesland nur übernehmen, wenn es ein bekanntes Land ist (kommt aus dem Import, nicht aus der Rechnung).
 */
export function resolveImportPcts(inp: Record<string, unknown>, analyzer: LegacyAnalyzer): PurchaseCostPcts {
  const version = typeof inp.purchaseCostsVersion === "number" ? inp.purchaseCostsVersion : 1;
  const base = version >= PURCHASE_COSTS_VERSION ? NK_DEFAULT_PCTS : LEGACY_NK_PCTS[analyzer];
  return {
    grest: pick(inp.nkGrEStPct, base.grest),
    notar: pick(inp.nkNotarPct, base.notar),
    grundbuch: pick(inp.nkGrundbuchPct, base.grundbuch),
    makler: pick(inp.nkMaklerPct, base.makler),
    sonstiges: pick(inp.nkSonstPct, base.sonstiges),
  };
}

/**
 * Baut den Input fuer *FromSnapshotInput()-Funktionen (z. B.
 * computeEtwDealScoreFromSnapshotInput/computeMfhDealScoreFromSnapshotInput) und
 * dealResultFromSnapshot.ts: die validierten/defaulteten Felder aus resolveXSnapshot()
 * (z. B. etwSnapshot.ts) PLUS die rohen Kaufnebenkosten-Felder (nkGrEStPct/...,
 * purchaseCostsVersion) aus dem tatsaechlich gespeicherten Datensatz -- die sind
 * BEWUSST NICHT Teil der getypten XSnapshotInput (eigenes Subsystem, siehe
 * exportPurchaseCosts/resolveImportPcts oben), gehen also beim reinen
 * resolveXSnapshot()-Aufruf verloren.
 *
 * Ohne diesen Merge liefert resolveImportPcts() (und jeder andere direkte Leser von
 * nkGrEStPct/...) IMMER die Default-/Legacy-Prozente statt der tatsaechlich
 * gespeicherten, regionalen Nebenkosten -- Nachtest 13.10.2026 (PHASE-4-BEFUND.md):
 * /deal/:id zeigte dadurch fuer ETW einen komplett falschen Cashflow (Darlehen
 * faelschlich als 0 behandelt, da computeEtwMetrics() mit undefined nk*Pct-Feldern
 * NaN statt 0 erhielt), und MFH/EFH/Gewerbe/Gemischt zeigten leise falsche (Default-
 * statt Ist-)Nebenkosten in Score/Cashflow -- unabhaengig von der konkreten Objektart,
 * ueberall dort, wo ein Deal per Snapshot neu berechnet wird (/check-Reload, /deal/:id,
 * "Meine Deals").
 *
 * Reihenfolge wichtig: rawInput zuerst, resolvedInput zuletzt -- resolvedInput's
 * bereits validierte/defaultete Felder duerfen NIE von unvalidierten Rohwerten
 * ueberschrieben werden; rawInput liefert nur die ZUSAETZLICHEN NK-Felder (und
 * purchaseCostsVersion fuer resolveImportPcts' Legacy-Erkennung), die resolvedInput
 * gar nicht kennt.
 */
export function withRawPurchaseCostFields<T extends Record<string, unknown>>(
  resolvedInput: T,
  rawInput: unknown
): T & Record<string, unknown> {
  const raw = rawInput && typeof rawInput === "object" && !Array.isArray(rawInput) ? (rawInput as Record<string, unknown>) : {};
  return { ...raw, ...resolvedInput };
}

/**
 * Export-Felder für JSON. Flache Namen bleiben wie bisher, zusätzlich Bundesland und Version.
 * `bundesland` ist nullable (noch nicht erkannt/gewählt, siehe usePurchaseCosts.ts) -- in der
 * Praxis sollte das Speichern/Exportieren so lange gesperrt sein (UI-Gate), nie `null` hier
 * ankommt; die leere Zeichenkette ist nur ein defensiver Fallback, kein stiller Berlin-Ersatz.
 */
export function exportPurchaseCosts(bundesland: string | null, p: PurchaseCostPcts) {
  return {
    purchaseCostsVersion: PURCHASE_COSTS_VERSION,
    bundesland: bundesland ?? "",
    nkGrEStPct: p.grest,
    nkNotarPct: p.notar,
    nkGrundbuchPct: p.grundbuch,
    nkMaklerPct: p.makler,
    nkSonstPct: p.sonstiges,
  };
}
